import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, GameSpeed } from '../game/types';

export class RecruitModal {
  private currentResearcher: Researcher | null = null;
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onConfirm: () => void,
    private onCancel: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Only handle if modal is open
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.confirm();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  private confirm(): void {
    const state = this.gameState.getState();
    const costs = this.gameData.costs;
    const canAfford = state.budget >= costs.recruit_cost;
    const canRecruit = this.gameState.canAddResearcher() && this.gameState.hasAvailableResearchers();

    if (canAfford && canRecruit) {
      this.onConfirm();
      this.hide();
    }
  }

  show(researcher: Researcher): void {
    this.currentResearcher = researcher;
    this.render();
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    // Attach keyboard handler to document when modal opens
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    // Focus the modal content to ensure it can receive focus
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    // Remove keyboard handler when modal closes
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    this.onCancel();
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(): void {
    if (!this.currentResearcher) return;

    const strings = this.gameData.uiStrings;
    const costs = this.gameData.costs;
    const state = this.gameState.getState();
    const canAfford = state.budget >= costs.recruit_cost;
    const canRecruit = this.gameState.canAddResearcher() && this.gameState.hasAvailableResearchers();

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content" tabindex="0">
        <div class="modal-header">
          <h2>${strings.modals?.recruit_title || 'Recruit Researcher'}</h2>
          <button class="modal-close" onclick="this.closest('.modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="recruit-info">
            <div class="info-item">
              <span>${strings.labels?.recruit_cost || 'Recruit Cost'}:</span>
              <span>${costs.recruit_cost} ${strings.labels?.budget || 'Budget'}</span>
            </div>
            <div class="info-item">
              <span>${strings.labels?.time || 'Time'}:</span>
              <span>${costs.recruit_time} ticks</span>
            </div>
            <div class="info-item">
              <span>${strings.labels?.available_resources || 'Available'}:</span>
              <span>${state.budget} ${strings.labels?.budget || 'Budget'}</span>
            </div>
            <div class="info-item">
              <span>Current Researchers:</span>
              <span>${state.researchers.length}/${this.gameData.gameConfig.max_researchers}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-cancel" data-action="cancel">${strings.buttons?.cancel || 'Cancel'}</button>
          <button class="button-confirm ${!canAfford || !canRecruit ? 'disabled' : ''}" 
                  ${!canAfford || !canRecruit ? 'disabled' : ''}
                  data-action="confirm">${strings.buttons?.confirm || 'Confirm'}</button>
        </div>
      </div>
    `;

    // Attach button event listeners
    const cancelButton = this.container.querySelector('[data-action="cancel"]') as HTMLElement;
    if (cancelButton) {
      cancelButton.addEventListener('click', () => {
        const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
        if (closeButton) closeButton.click();
      });
    }

    const confirmButton = this.container.querySelector('[data-action="confirm"]') as HTMLElement;
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        if (canAfford && canRecruit) {
          this.onConfirm();
          this.hide();
        }
      });
    }
  }
}


